// frontend/src/ComplianceCheck.tsx - UPDATED with No Default Selection Approach

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import { API_URL } from './apiConfig';

// --- Component Props and Interfaces ---

interface ComplianceCheckProps {
  processId: number;
  processName: string;
  onComplete: () => void;
  onBack: () => void;
}

interface ComplianceCriterion {
  id: number;
  description: string;
  order_index: number;
}

interface Supplier {
  id: number;
  name: string;
  abn_acn: string;
}

interface ComplianceCheck {
  criterion_id: number;
  is_compliant: boolean | null;
  comments: string;
}

// Map of supplier IDs to their compliance status
interface SupplierComplianceMap {
  [key: number]: {
    is_fully_compliant: boolean;
    checks: { [key: number]: ComplianceCheck }; // criterion_id -> check
  };
}

const styles = {
    mainContainer: {
      minHeight: '100vh',
      background: 'var(--background-brand-gradient)',
      fontFamily: 'var(--font-family-primary)',
      color: 'var(--color-text-primary)',
      padding: 'var(--spacing-lg)'
    },
    contentWrapper: {
      maxWidth: '1200px',
      margin: '0 auto'
    },
    pageTitle: {
      fontSize: 'var(--font-size-headline-large)',
      fontWeight: 'var(--font-weight-headline)',
      color: 'var(--color-text-on-gradient)',
      marginBottom: 'var(--spacing-xl)',
      textAlign: 'center' as 'center'
    },
    card: {
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      backdropFilter: 'blur(10px)',
      padding: 'var(--spacing-xl)',
      borderRadius: 'var(--border-radius-lg)',
      boxShadow: 'var(--shadow-lg)',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      marginBottom: 'var(--spacing-xl)'
    },
    button: {
      padding: 'var(--spacing-sm) var(--spacing-md)',
      background: 'var(--color-primary)',
      color: 'white',
      border: 'none',
      borderRadius: 'var(--border-radius-md)',
      fontSize: 'var(--font-size-sm)',
      fontWeight: 'var(--font-weight-medium)',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 'var(--spacing-xs)'
    },
    buttonSecondary: {
        background: 'var(--color-surface-variant)',
        color: 'var(--color-text-primary)',
        border: '2px solid var(--color-primary)',
    },
    buttonDanger: {
        background: 'var(--color-error)',
        color: 'white'
    },
    passFailButton: {
        padding: 'var(--spacing-sm)',
        border: '2px solid var(--color-surface-variant)',
        borderRadius: 'var(--border-radius-sm)',
        fontWeight: 'var(--font-weight-bold)',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        flex: 1,
    },
    passButtonActive: {
        background: 'var(--color-success)',
        color: 'white',
        borderColor: 'var(--color-success)',
    },
    failButtonActive: {
        background: 'var(--color-error)',
        color: 'white',
        borderColor: 'var(--color-error)',
    },
    buttonInactive: {
        background: 'var(--color-surface-variant)',
        color: 'var(--color-text-secondary)',
    },
    input: {
      width: '100%',
      padding: 'var(--spacing-sm)',
      border: '1px solid var(--color-surface-variant)',
      borderRadius: 'var(--border-radius-sm)',
      fontSize: 'var(--font-size-sm)',
      fontFamily: 'var(--font-family-primary)',
      transition: 'border-color 0.2s ease',
      outline: 'none',
      boxSizing: 'border-box' as const
    },
    label: {
      fontSize: 'var(--font-size-sm)',
      fontWeight: 'var(--font-weight-medium)',
      color: 'var(--color-text-primary)',
      marginBottom: 'var(--spacing-xs)',
      display: 'block'
    },
};

const handleButtonHover = (e: React.MouseEvent<HTMLButtonElement>, enter: boolean) => {
    e.currentTarget.style.transform = enter ? 'translateY(-2px)' : 'translateY(0)';
    e.currentTarget.style.boxShadow = enter ? '0 10px 15px -3px rgba(0, 0, 0, 0.1)' : 'none';
};

const ComplianceCheck: React.FC<ComplianceCheckProps> = ({ 
  processId, 
  processName, 
  onComplete, 
  onBack 
}) => {
  const [currentStep, setCurrentStep] = useState<'setup' | 'check'>('setup');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [notification, setNotification] = useState<{type: 'success' | 'error', message: string} | null>(null);

  const [criteria, setCriteria] = useState<{description: string}[]>([
    { description: "Valid ABN/ACN registration" },
    { description: "Current Public Liability Insurance" },
    { description: "Compliance with Ethical Supplier Threshold" },
  ]);

  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [existingCriteria, setExistingCriteria] = useState<ComplianceCriterion[]>([]);
  const [supplierCompliance, setSupplierCompliance] = useState<SupplierComplianceMap>({});
  const [needsRefresh, setNeedsRefresh] = useState(true); // State to trigger data loading

  const showNotification = useCallback((type: 'success' | 'error', message: string) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 5000);
  }, []);

  const saveCheck = useCallback(async (supplierId: number, criterionId: number, check: ComplianceCheck) => {
    try {
      const token = Cookies.get('access_token');
      const payload = {
        process_id: processId,
        supplier_id: supplierId,
        criterion_id: criterionId,
        is_compliant: check.is_compliant,
        comments: check.comments
      };
      await axios.post(`${API_URL}/api/compliance/checks`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error: any) {
      console.error('Failed to save check:', error);
      showNotification('error', `Failed to save check for criterion ${criterionId}.`);
      throw error;
    }
  }, [processId, showNotification]);

  useEffect(() => {
    const loadAllData = async () => {
        if (!needsRefresh) return;

        setIsLoadingData(true);
        try {
            const token = Cookies.get('access_token');
            const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

            const [processDataResponse, suppliersResponse] = await Promise.all([
                axios.get(`${API_URL}/api/processes/${processId}/complete-data`, authHeaders),
                axios.get(`${API_URL}/api/processes/${processId}/suppliers`, authHeaders)
            ]);

            const loadedCriteria = processDataResponse.data.compliance_criteria || [];
            const loadedSuppliers: Supplier[] = suppliersResponse.data || [];
            
            setSuppliers(loadedSuppliers);
            setExistingCriteria(loadedCriteria);

            if (loadedCriteria.length > 0) {
                const complianceStatusResponse = await axios.get(`${API_URL}/api/compliance/processes/${processId}/suppliers/status`, authHeaders);
                const statuses = complianceStatusResponse.data.suppliers_status || [];
                
                const complianceMap: SupplierComplianceMap = {};
                statuses.forEach((status: any) => {
                    const supplierChecks: { [key: number]: ComplianceCheck } = {};
                    if (Array.isArray(status.checks)) {
                        status.checks.forEach((checkData: any) => {
                            supplierChecks[checkData.criterion_id] = {
                                criterion_id: checkData.criterion_id,
                                is_compliant: checkData.is_compliant,
                                comments: checkData.comments || ''
                            };
                        });
                    }
                    complianceMap[status.supplier_id] = {
                        is_fully_compliant: status.is_fully_compliant,
                        checks: supplierChecks
                    };
                });

                // Initialize suppliers without existing data - but with NO defaults
                loadedSuppliers.forEach((supplier: Supplier) => {
                    if (!complianceMap[supplier.id]) {
                        complianceMap[supplier.id] = {
                            is_fully_compliant: false,
                            checks: {}
                        };
                    }
                });

                setSupplierCompliance(complianceMap);
                setCurrentStep('check');
            } else {
                setCurrentStep('setup');
            }
        } catch (error: any) {
            console.error('Error loading process data:', error);
            showNotification('error', 'Failed to load process data. Please try again.');
        } finally {
            setIsLoadingData(false);
            setNeedsRefresh(false); // Mark as clean
        }
    };
    
    loadAllData();
  }, [processId, showNotification, saveCheck, needsRefresh]);

  const handleCriterionChange = (index: number, value: string) => {
    const newCriteria = [...criteria];
    newCriteria[index].description = value;
    setCriteria(newCriteria);
  };

  const addCriterion = () => {
    setCriteria([...criteria, { description: "" }]);
  };

  const removeCriterion = (index: number) => {
    if (criteria.length > 1) {
      setCriteria(criteria.filter((_, i) => i !== index));
    } else {
      showNotification('error', 'At least one compliance criterion is required.');
    }
  };

  const saveCriteria = async () => {
    setIsLoading(true);
    try {
      const token = Cookies.get('access_token');
      const validCriteria = criteria
        .map((c, index) => ({ description: c.description, order_index: index + 1 }))
        .filter(c => c.description.trim() !== '');
      
      if (validCriteria.length === 0) {
        showNotification('error', 'Please add at least one compliance criterion.');
        setIsLoading(false);
        return;
      }

      const apiPayload = { criteria: validCriteria };
      await axios.post(`${API_URL}/api/processes/${processId}/compliance-criteria`, apiPayload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showNotification('success', 'Compliance criteria saved successfully!');
      setNeedsRefresh(true);
    } catch (error: any) {
      console.error('Error saving criteria:', error);
      showNotification('error', error.response?.data?.detail || 'Failed to save criteria. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCheckChange = async (supplierId: number, criterionId: number, field: 'is_compliant' | 'comments', value: boolean | string) => {
    setIsSaving(true);
    
    // Use functional update to ensure we have the latest state
    setSupplierCompliance(currentState => {
        const newState = JSON.parse(JSON.stringify(currentState));
        const supplierData = newState[supplierId];

        if (!supplierData) return currentState; // Should not happen

        if (!supplierData.checks[criterionId]) {
            supplierData.checks[criterionId] = { criterion_id: criterionId, is_compliant: null, comments: '' };
        }
        
        supplierData.checks[criterionId][field] = value;
        
        // Recalculate compliance status - now requires both decision and comments
        const hasAllSelections = existingCriteria.every(criterion => {
            const check = supplierData.checks[criterion.id];
            return check && 
                   check.is_compliant !== null && 
                   check.comments && 
                   check.comments.trim() !== '';
        });

        if (hasAllSelections) {
            supplierData.is_fully_compliant = existingCriteria.every(criterion => {
                const check = supplierData.checks[criterion.id];
                return check && check.is_compliant === true;
            });
        } else {
            supplierData.is_fully_compliant = false;
        }
        
        // Save the change to the backend - check if both fields are complete
        const currentCheck = supplierData.checks[criterionId];
        const hasCompliance = currentCheck.is_compliant !== null;
        const hasComments = currentCheck.comments && currentCheck.comments.trim() !== '';
        
        if (hasCompliance && hasComments) {
            // Both fields complete - save to backend
            saveCheck(supplierId, criterionId, currentCheck)
                .finally(() => setIsSaving(false));
        } else if (hasCompliance && !hasComments) {
            // Has compliance decision but no comments - show error
            showNotification('error', 'Comments are mandatory for all compliance decisions. Please provide justification.');
            setIsSaving(false);
        } else {
            // Either incomplete or just comments without compliance decision - don't save yet
            setIsSaving(false);
        }

        return newState;
    });
  };

  const getOverallComplianceStatus = () => {
    if (suppliers.length === 0) return { compliant: 0, pending: 0, nonCompliant: 0, total: 0, allComplete: false };
    
    let compliant = 0;
    let pending = 0;
    let nonCompliant = 0;
    let allComplete = true;

    suppliers.forEach(supplier => {
        const complianceInfo = supplierCompliance[supplier.id];
        if (!complianceInfo) {
            pending++;
            allComplete = false;
            return;
        }

        const hasAllSelections = existingCriteria.every(criterion => {
            const check = complianceInfo.checks[criterion.id];
            return check && 
                   check.is_compliant !== null && 
                   check.comments && 
                   check.comments.trim() !== '';
        });

        if (!hasAllSelections) {
            pending++;
            allComplete = false;
        } else {
            const isFullyCompliant = existingCriteria.every(criterion => {
                const check = complianceInfo.checks[criterion.id];
                return check && check.is_compliant === true;
            });
            
            if (isFullyCompliant) {
                compliant++;
            } else {
                nonCompliant++;
            }
        }
    });

    return { compliant, pending, nonCompliant, total: suppliers.length, allComplete };
  };

  if (isLoadingData) {
    return (
      <div style={styles.mainContainer} className="density-compact">
        <div style={styles.contentWrapper}>
          <h1 style={styles.pageTitle}>PS Compliance Check: {processName}</h1>
          <div style={{ textAlign: 'center' }}>
            <div style={{...styles.card, maxWidth: '400px', margin: 'auto'}}>
              <div style={{ fontSize: 'var(--font-size-lg)', color: 'var(--color-text-primary)' }}>Loading Compliance Data...</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const complianceStatus = getOverallComplianceStatus();

  return (
    <div style={styles.mainContainer} className="density-compact">
      {notification && (
        <div style={{
          position: 'fixed', top: '1rem', right: '1rem', zIndex: 1000,
          background: notification.type === 'success' ? 'var(--color-success)' : 'var(--color-error)',
          color: 'white', padding: '1rem 1.5rem', borderRadius: '12px',
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
        }}>
          {notification.message}
        </div>
      )}
      
      <div style={styles.contentWrapper}>
        <h1 style={styles.pageTitle}>PS Compliance Check: {processName}</h1>
        <p style={{ textAlign: 'center', color: 'var(--color-text-on-gradient-secondary)', marginBottom: 'var(--spacing-xl)' }}>
          Process ID: {processId} {isSaving && '• Saving...'}
        </p>
        
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          {currentStep === 'setup' && (
            <div style={styles.card}>
              <h2 style={{ color: '#1e293b', marginBottom: '0.5rem', fontSize: '1.5rem', fontWeight: 'bold' }}>1. Setup Compliance Criteria</h2>
              <p style={{ color: '#6b7280', marginBottom: '2rem' }}>Define the pass/fail criteria for this process.</p>
              <div style={{ marginBottom: '2rem' }}>
                {criteria.map((criterion, index) => (
                  <div key={index} style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', alignItems: 'center' }}>
                    <input type="text" value={criterion.description} onChange={(e) => handleCriterionChange(index, e.target.value)} placeholder="e.g., Has valid Public Liability Insurance" style={styles.input} />
                    <button onClick={() => removeCriterion(index)} style={{...styles.button, ...styles.buttonDanger}} disabled={criteria.length === 1}>Remove</button>
                  </div>
                ))}
              </div>
              <button onClick={addCriterion} style={{...styles.button, background: 'linear-gradient(45deg, #10b981, #059669)'}}>+ Add Criterion</button>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '2rem', paddingTop: '2rem', borderTop: '1px solid #e2e8f0' }}>
                <button onClick={saveCriteria} disabled={isLoading} style={{...styles.button, ...(isLoading && { background: '#9ca3af', cursor: 'not-allowed' })}}>
                  {isLoading ? 'Saving...' : 'Save Criteria & Continue →'}
                </button>
              </div>
            </div>
          )}

          {currentStep === 'check' && (
            <div style={styles.card}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <h2 style={{ color: '#1e293b', margin: 0, fontSize: '1.5rem', fontWeight: 'bold' }}>2. Perform Compliance Checks</h2>
                <button onClick={() => setCurrentStep('setup')} style={{...styles.button, ...styles.buttonSecondary}}>← Edit Criteria</button>
              </div>
              <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px', padding: '1rem', marginBottom: '2rem' }}>
                <p style={{ margin: 0, color: '#92400e', fontWeight: 'bold' }}>📋 Instructions:</p>
                <p style={{ margin: '0.5rem 0 0 0', color: '#92400e', fontSize: '0.9rem' }}>
                  Fields marked with <span style={{ color: '#ef4444', fontWeight: 'bold' }}>*</span> and red borders are mandatory. 
                  All compliance decisions must include detailed justification comments before the process can be completed.
                </p>
              </div>
              <p style={{ color: '#6b7280', marginBottom: '2rem' }}>Evaluate each supplier against the criteria. Changes are automatically saved.</p>
              
              {suppliers.length === 0 ? (
                <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '12px', padding: '1.5rem', textAlign: 'center' }}>
                  <h3 style={{ color: '#92400e', margin: '0 0 0.5rem 0' }}>No Suppliers Added</h3>
                  <p style={{ color: '#92400e', margin: 0 }}>Please add suppliers to this process before performing compliance checks.</p>
                </div>
              ) : (
                <div style={{ display: 'grid', gap: '1.5rem' }}>
                  {suppliers.map(supplier => {
                    const complianceInfo = supplierCompliance[supplier.id] || { is_fully_compliant: false, checks: {} };
                    
                    const hasAllSelections = existingCriteria.every(criterion => {
                        const check = complianceInfo.checks[criterion.id];
                        return check && 
                               check.is_compliant !== null && 
                               check.comments && 
                               check.comments.trim() !== '';
                    });

                    const isFullyCompliant = hasAllSelections && existingCriteria.every(criterion => {
                        const check = complianceInfo.checks[criterion.id];
                        return check && check.is_compliant === true;
                    });

                    const hasAnyFailures = existingCriteria.some(criterion => {
                        const check = complianceInfo.checks[criterion.id];
                        return check && check.is_compliant === false;
                    });
                    
                    return (
                      <div key={supplier.id} style={{ 
                          border: `2px solid ${!hasAllSelections ? '#d1d5db' : (isFullyCompliant ? '#10b981' : '#ef4444')}`, 
                          borderRadius: '12px', 
                          padding: '1.5rem',
                          background: !hasAllSelections ? '#ffffff' : (isFullyCompliant ? '#f0fdf4' : '#fef2f2'),
                          transition: 'all 0.3s'
                        }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                          <h3 style={{ margin: 0, color: '#1e293b' }}>{supplier.name}</h3>
                          <div style={{ 
                            background: !hasAllSelections ? '#6b7280' : (isFullyCompliant ? '#10b981' : '#ef4444'), 
                            color: 'white', 
                            padding: '0.25rem 0.75rem', 
                            borderRadius: '9999px', 
                            fontSize: '0.8rem', 
                            fontWeight: 'bold' 
                          }}>
                            {!hasAllSelections ? '○ PENDING' : (isFullyCompliant ? '✓ COMPLIANT' : '✗ NON-COMPLIANT')}
                          </div>
                        </div>
                        <p style={{ margin: '0 0 1.5rem 0', color: '#6b7280', fontSize: '0.9rem' }}>ABN/ACN: {supplier.abn_acn}</p>
                        
                        {existingCriteria.map(criterion => {
                          const check = complianceInfo.checks[criterion.id] || { criterion_id: criterion.id, is_compliant: null, comments: '' };
                          return (
                            <div key={criterion.id} style={{ background: '#f8fafc', padding: '1rem', borderRadius: '8px', border: '1px solid #e2e8f0', marginBottom: '1rem' }}>
                              <strong style={{ color: '#374151', display: 'block', marginBottom: '0.75rem' }}>{criterion.description}</strong>
                              <div style={{ display: 'flex', gap: '1rem', marginBottom: '0.75rem' }}>
                                <button 
                                  onClick={() => handleCheckChange(supplier.id, criterion.id, 'is_compliant', true)} 
                                  style={{
                                    ...styles.passFailButton, 
                                    ...(check.is_compliant === true ? styles.passButtonActive : styles.buttonInactive)
                                  }}
                                >
                                  Pass
                                </button>
                                <button 
                                  onClick={() => handleCheckChange(supplier.id, criterion.id, 'is_compliant', false)} 
                                  style={{
                                    ...styles.passFailButton, 
                                    ...(check.is_compliant === false ? styles.failButtonActive : styles.buttonInactive)
                                  }}
                                >
                                  Fail
                                </button>
                              </div>
                              <div>
                                <label style={{
                                  display: 'block',
                                  marginBottom: '0.5rem',
                                  fontWeight: 'bold',
                                  color: '#374151'
                                }}>
                                  Comments & Justification: <span style={{ color: '#ef4444' }}>*</span>
                                </label>
                                <textarea 
                                  value={check.comments} 
                                  onChange={(e) => handleCheckChange(supplier.id, criterion.id, 'comments', e.target.value)} 
                                  placeholder="Provide detailed justification for your compliance decision (required)..." 
                                  rows={2} 
                                  style={{
                                    ...styles.input, 
                                    fontSize: '0.9rem', 
                                    padding: '8px 12px',
                                    border: (!check.comments || check.comments.trim() === '') 
                                      ? '2px solid #fecaca' 
                                      : '2px solid #d1fae5'
                                  }} 
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              )}
              
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '2rem', paddingTop: '2rem', borderTop: '1px solid #e2e8f0' }}>
                <div>
                  <strong style={{ color: '#374151' }}>Compliance Summary:</strong>
                  <p style={{ margin: '0.25rem 0 0 0', color: '#6b7280' }}>
                    {complianceStatus.compliant} compliant • {complianceStatus.pending} pending • {complianceStatus.nonCompliant} non-compliant • {complianceStatus.total} total
                  </p>
                  {!complianceStatus.allComplete && (
                    <p style={{ margin: '0.5rem 0 0 0', color: '#ef4444', fontSize: '0.9rem', fontWeight: 'bold' }}>
                      ⚠️ Complete all compliance decisions and comments before proceeding
                    </p>
                  )}
                </div>
                <button 
                  onClick={() => {
                    if (!complianceStatus.allComplete) {
                      showNotification('error', 'All compliance decisions must include comments before the process can be completed.');
                      return;
                    }
                    onComplete();
                  }} 
                  disabled={!complianceStatus.allComplete}
                  style={{
                    ...styles.button, 
                    background: complianceStatus.allComplete 
                      ? 'linear-gradient(45deg, #10b981, #059669)' 
                      : '#d1d5db',
                    color: complianceStatus.allComplete ? 'white' : '#6b7280',
                    cursor: complianceStatus.allComplete ? 'pointer' : 'not-allowed'
                  }}
                >
                  {complianceStatus.allComplete ? 'Complete Compliance Process ✅' : 'Complete All Fields First'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ComplianceCheck;
